import { File } from '@/types/folders/folder';

export class FileStore {
  private files: Map<string, File> = new Map();
  private counter: number = 1;

  create(data: {
    name: string;
    parent_id?: string;
    tags?: string[];
    type?: string;
  }): File {
    const now = new Date().toISOString();
    const file: File = {
      id: `file_${this.counter++}`,
      name: data.name,
      type: data.type || 'pdf',
      path: `/mock/path/${data.name}`,
      created_at: now,
      updated_at: now,
      created_by: 'user_mock',
      tags: data.tags || [],
      processed: false,
      default: false,
      extras: {},
      parent_id: data.parent_id || '',
      project_id: null,
      deleted_at: null,
    };
    this.files.set(file.id, file);
    return file;
  }

  getAll(params?: {
    parent_id?: string;
    organization_id?: string;
    category_id?: string;
    project_id?: string;
  }): File[] {
    const files: File[] = [];

    for (const file of this.files.values()) {
      if (file.deleted_at) continue;

      if (params?.parent_id !== undefined) {
        if (file.parent_id === params.parent_id) {
          files.push(file);
        }
      } else {
        if (!file.parent_id) {
          files.push(file);
        }
      }
    }

    return files;
  }

  getById(fileId: string): File | null {
    const file = this.files.get(fileId);
    return file && !file.deleted_at ? file : null;
  }

  delete(fileId: string): boolean {
    const file = this.files.get(fileId);
    if (!file) return false;

    file.deleted_at = new Date().toISOString();
    file.updated_at = new Date().toISOString();
    this.files.set(fileId, file);
    return true;
  }

  deleteByParentId(parentId: string): void {
    for (const file of this.files.values()) {
      if (file.parent_id === parentId && !file.deleted_at) {
        this.delete(file.id);
      }
    }
  }

  rename(fileId: string, fileName: string): File | null {
    const file = this.files.get(fileId);
    if (!file || file.deleted_at) return null;

    file.name = fileName;
    file.updated_at = new Date().toISOString();
    this.files.set(fileId, file);
    return file;
  }

  move(fileId: string, targetFolderId: string | null): boolean {
    const file = this.files.get(fileId);
    if (!file || file.deleted_at) return false;

    file.parent_id = targetFolderId || '';
    file.updated_at = new Date().toISOString();
    this.files.set(fileId, file);
    return true;
  }

  updateTags(fileId: string, tags: string[]): File | null {
    const file = this.files.get(fileId);
    if (!file || file.deleted_at) return null;

    file.tags = tags;
    file.updated_at = new Date().toISOString();
    this.files.set(fileId, file);
    return file;
  }

  getChildren(parentId: string | null): File[] {
    return this.getAll({ parent_id: parentId ?? undefined });
  }

  clear(): void {
    this.files.clear();
    this.counter = 1;
  }

  getAllActive(): File[] {
    return Array.from(this.files.values()).filter(f => !f.deleted_at);
  }

  initializeDefaultData(folderMap: Map<string, string>): void {
    const documentsId = folderMap.get('Documents');
    const reportsId = folderMap.get('Reports');
    const templatesId = folderMap.get('Templates');
    const project2024Id = folderMap.get('Project 2024');
    const archiveId = folderMap.get('Archive');

    this.create({
      name: 'README.pdf',
      parent_id: '',
      type: 'pdf',
      tags: ['important', 'documentation'],
    });
    this.create({
      name: 'Guidelines.docx',
      parent_id: '',
      type: 'docx',
      tags: ['guidelines'],
    });

    if (documentsId) {
      this.create({
        name: 'Meeting Notes.pdf',
        parent_id: documentsId,
        type: 'pdf',
        tags: ['meeting', 'notes'],
      });
      this.create({
        name: 'Company Policy.docx',
        parent_id: documentsId,
        type: 'docx',
        tags: ['policy'],
      });
    }

    if (reportsId) {
      this.create({
        name: 'Monthly Report - January.pdf',
        parent_id: reportsId,
        type: 'pdf',
        tags: ['report', 'monthly'],
      });
      this.create({
        name: 'Annual Summary 2023.pdf',
        parent_id: reportsId,
        type: 'pdf',
        tags: ['report', 'annual'],
      });
    }

    if (templatesId) {
      this.create({
        name: 'Email Template.txt',
        parent_id: templatesId,
        type: 'txt',
        tags: ['template', 'email'],
      });
      this.create({
        name: 'Report Template.docx',
        parent_id: templatesId,
        type: 'docx',
        tags: ['template', 'report'],
      });
    }

    if (project2024Id) {
      this.create({
        name: 'Project Plan.pdf',
        parent_id: project2024Id,
        type: 'pdf',
        tags: ['project', 'plan'],
      });
      this.create({
        name: 'Requirements.docx',
        parent_id: project2024Id,
        type: 'docx',
        tags: ['project', 'requirements'],
      });
    }

    if (archiveId) {
      this.create({
        name: 'Old Document 2022.pdf',
        parent_id: archiveId,
        type: 'pdf',
        tags: ['archive', '2022'],
      });
    }
  }
}
